/* One source switch for every storefront combinables consumer. */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.RockEmCombinables = factory(root);
})(typeof globalThis !== 'undefined' ? globalThis : this, function (root) {
  'use strict';
  function parse(value) {
    if (typeof value === 'string') { try { return JSON.parse(value); } catch (_) { return null; } }
    return value && typeof value === 'object' ? value : null;
  }
  function mode(value) { const selected = value || root?.__COMBINABLES_SOURCE_MODE__ || 'auto'; return ['shopify', 'api'].includes(selected) ? selected : 'auto'; }
  function isV2Jersey(product) {
    const variants = product?.variants?.nodes || product?.variants || [];
    const identity = [product?.handle, product?.title, product?.sku, product?.template_suffix,
      product?.metafields?.jersey_style, product?.metafields?.custom?.jersey_style?.value,
      ...variants.map(variant => typeof variant === 'string' ? variant : variant?.sku)].join(' ').toLowerCase();
    return identity.includes('jrsyv2') || identity.includes('custom authentic jersey')
      || identity.includes('custom-nfl-jersey26') || identity.includes('nfl-custom-jersey-2026');
  }
  function isWhiteSwatch(product) {
    const color = String(product?.swatchColor || product?.color || product?.hex || '').trim();
    // Explicit swatch colors take precedence over a product's name.
    if (color) return /^(white|#fff(?:fff)?|#ffffffff|rgb\(\s*255\s*,\s*255\s*,\s*255\s*\))$/i.test(color);
    const title = String(product?.title || '');
    const design = (title.split(' - ').pop() || '').trim()
      .replace(/\s*\((?:crew|quarter|ankle|no[ -]?show|knee[ -]?high|over[ -]the[ -]calf)\)\s*$/i, '').trim();
    const customFace = /^custom-face-socks-/i.test(product?.handle || '') || /^custom face socks?\s*-/i.test(title);
    return /\(\s*white\s*\)/i.test(title) || /^white(?: out)?$/i.test(design)
      || (customFace && /\bwhite$/i.test(design) && !/\b(?:off|snow)[ -]+white$/i.test(design))
      || (/^custom-face-socks-(?:[a-z0-9]+-)*white(?:-\d+)?$/i.test(product?.handle || '')
        && !/-(?:off|snow)-white(?:-\d+)?$/i.test(product?.handle || ''))
      || /(?:jersey|logo-sketch|cotton-casuals|basics)-white(?:-(?:crew|quarter|ankle|no-show))?(?:-\d+)?$/i.test(product?.handle || '');
  }
  function empty(sku) { return { schemaVersion: 2, sku: sku || '', results: [], matchingSets: [] }; }
  function snapshot(value, sku) {
    const data = parse(value);
    return data && Array.isArray(data.results) ? data : empty(sku);
  }
  async function hydrateMatchingSets(data, fetcher) {
    const products = new Map();
    const sets = data.matchingSets || [];
    await Promise.all(sets.flatMap(set => set.items || []).map(async item => {
      const handle = item.product?.handle;
      if (!handle || products.has(handle)) return;
      const request = (async () => {
        const prefix = root?.Shopify?.routes?.root || '/';
        const response = await fetcher(prefix + 'products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' });
        if (!response.ok) return null;
        const p = await response.json();
        return { ...p, id: 'gid://shopify/Product/' + p.id, variants: (p.variants || []).map(v => ({
          ...v, id: 'gid://shopify/ProductVariant/' + v.id,
          price: String(Number(v.price) / 100), compareAtPrice: v.compare_at_price == null ? null : String(Number(v.compare_at_price) / 100)
        })) };
      })().catch(() => null);
      products.set(handle, request);
    }));
    return { ...data, matchingSets: await Promise.all(sets.map(async set => ({ ...set,
      items: await Promise.all((set.items || []).map(async item => ({ ...item, product: await products.get(item.product?.handle) || null })))
    }))) };
  }
  async function load(options) {
    const fetcher = options.fetcher || root.fetch.bind(root);
    const selected = mode(options.mode), saved = parse(options.snapshot);
    if (selected === 'shopify' || (selected === 'auto' && Array.isArray(saved?.results))) {
      return hydrateMatchingSets(snapshot(saved, options.sku), fetcher);
    }
    if (!options.endpoint || !options.sku) return empty(options.sku);
    const url = options.endpoint + (options.endpoint.includes('?') ? '&' : '?') + 'sku=' + encodeURIComponent(options.sku);
    const response = await fetcher(url, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Combinables request failed (' + response.status + ')');
    return response.json();
  }
  function collection(products) {
    const results = {}, stars = new Set(), groups = new Map();
    const cards = new Map((products || []).map(product => [product.handle, {
      product, data: parse(product.metafields?.combinables_card || product.combinablesCard)
    }]));
    (products || []).forEach(product => {
      const data = cards.get(product.handle).data;
      if (!data || !Array.isArray(data.handles) || !data.handles.length) return;
      // Older saved cards contain every tab of a matching-set profile. Use
      // Shopify's format to keep those garments separate until republished.
      // New cards already carry the exact tab membership and are authoritative.
      const format = !data.tabId && String(product.metafields?.format || '').trim().toLowerCase();
      const handles = data.handles.filter(handle => {
        const memberFormat = String(cards.get(handle)?.product.metafields?.format || '').trim().toLowerCase();
        return !format || !memberFormat || memberFormat === format;
      });
      const key = JSON.stringify([data.groupId || '', data.tabId || '', handles]);
      if (!groups.has(key)) {
        const members = handles.map(handle => cards.get(handle)).filter(Boolean).map(({ product: p, data: own }) => ({
          id: p.id, handle: p.handle, title: p.title, sku: own?.sku || p.sku || p.variants?.[0]?.sku,
          variants: p.variants || [], swatchUrl: own?.swatchUrl || (isV2Jersey(p) ? '' : p.image?.url) || '', image: p.image
        }));
        const skus = new Set(members.flatMap(member => [member.sku, ...member.variants.map(v => typeof v === 'string' ? v : v.sku)]).filter(Boolean));
        const leadSku = skus.has(data.leadSku) ? data.leadSku : members[0]?.sku;
        // Keep membership even when Shopify omits a product from this view.
        // Boost can still render its card; it must not become a standalone
        // product just because it cannot supply an available swatch here.
        groups.set(key, { stars: new Set(), skus, payload: { count: handles.length, leadSku, memberHandles: handles, firstFourProducts: members } });
      }
      const group = groups.get(key);
      (data.starredSkus || []).forEach(sku => {
        // Exact-tab metadata already scopes stars to that list. Preserve a
        // saved star even when its product is missing from this response.
        if (data.tabId || group.skus.has(sku)) { stars.add(sku); group.stars.add(sku); }
      });
      const sku = product.sku || (product.variants || []).map(v => typeof v === 'string' ? v : v.sku).find(Boolean);
      if (sku) results[sku] = group.payload;
    });
    groups.forEach(group => { group.payload.starredSkus = [...group.stars]; });
    return { results, starredSkus: [...stars] };
  }

  return { mode, parse, snapshot, empty, load, collection, isWhiteSwatch, isV2Jersey };
});
